import { Card, CircularProgress, Stack, Typography } from '@healthinal/ui';
import { useAtom } from 'jotai';
import { type RefObject, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { toTaskDueDateQueryParam, useGetPendingTasksInfiniteSuspense } from '../../../api/created/tasks.ts';
import { TaskDto, TaskDtoTaskType } from '../../../api/generated/model';
import { useGetPatientBasicsById } from '../../../api/generated/patient-controller/patient-controller.ts';
import { useCustomVirtualizer } from '../../../hooks/useCustomVirtualizer.ts';
import {
  taskAssignedEmployeeIdAtom,
  taskDueDateFilterAtom,
  taskFilterAtom,
  taskIncludeCompletedAtom,
  taskSelectedPatientFilterAtom,
  taskSortDirAtom,
} from '../../../state/taskSearchAtoms.ts';
import { TASK_CARD_HEIGHT, theme } from '../../../theme.ts';
import { filterTasks } from '../../../utils/taskSearch/taskFilter.ts';
import { TaskAppointmentCard } from './TaskAppointmentCard.tsx';
import { TaskJournalCard } from './TaskJournalCard.tsx';
import { TaskLabOrderCard } from './TaskLabOrderCard.tsx';
import { TaskNotic } from './TaskNotic.tsx';
import { TaskTherapyComplianceOverdueCard } from './TaskTherapyComplianceOverdueCard.tsx';
import { TaskWaitingPeriodOverCard } from './TaskWaitingPeriodOverCard.tsx';

interface Props {
  contentSectionRef: RefObject<HTMLDivElement | null>;
}

export function TaskList({ contentSectionRef }: Props) {
  const [selectedPatient] = useAtom(taskSelectedPatientFilterAtom);
  const [selectedTaskFilter] = useAtom(taskFilterAtom);
  const [includeCompleted] = useAtom(taskIncludeCompletedAtom);
  const [dueDate] = useAtom(taskDueDateFilterAtom);
  const [assignedEmployeeId] = useAtom(taskAssignedEmployeeIdAtom);
  const [sortDir] = useAtom(taskSortDirAtom);
  const dueDateParam = toTaskDueDateQueryParam(dueDate);

  const { tasks, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useGetPendingTasksInfiniteSuspense({
    ...(selectedPatient ? { patientId: selectedPatient.id } : {}),
    ...(includeCompleted ? { includeCompleted: true } : {}),
    ...(dueDateParam ? { dueDate: dueDateParam } : {}),
    ...(assignedEmployeeId ? { assignedEmployeeId } : {}),
    sortBy: 'due',
    sortDir,
  });

  const previousScrollTopRef = useRef(0);
  const isLoadingNextPageRef = useRef(false);

  const filteredTasks = useMemo(() => filterTasks(tasks, selectedTaskFilter), [tasks, selectedTaskFilter]);

  useLayoutEffect(() => {
    const element = contentSectionRef.current;
    if (element) {
      element.scrollTop = 0;
    }
    previousScrollTopRef.current = 0;
  }, [assignedEmployeeId, contentSectionRef, dueDateParam, includeCompleted, selectedPatient?.id, sortDir]);

  useEffect(() => {
    const element = contentSectionRef.current;
    if (!element) {
      return;
    }

    const handleScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = element;
      const isScrollingDown = scrollTop > previousScrollTopRef.current;
      previousScrollTopRef.current = scrollTop;

      const reachedBottom = scrollTop + clientHeight >= scrollHeight - 24;
      if (
        !isScrollingDown ||
        !reachedBottom ||
        scrollHeight <= clientHeight ||
        !hasNextPage ||
        isFetching ||
        isLoadingNextPageRef.current
      ) {
        return;
      }

      isLoadingNextPageRef.current = true;
      void fetchNextPage().finally(() => {
        isLoadingNextPageRef.current = false;
      });
    };

    element.addEventListener('scroll', handleScroll);
    return () => element.removeEventListener('scroll', handleScroll);
  }, [contentSectionRef, fetchNextPage, hasNextPage, isFetching]);

  const { virtualItems, totalSize, paddingTop, paddingBottom, measureElement } = useCustomVirtualizer({
    count: filteredTasks.length,
    cellHeight: TASK_CARD_HEIGHT,
    parentRef: contentSectionRef,
    overscan: 30,
  });

  return (
    <>
      {!filteredTasks.length && (
        <Stack alignItems={'center'} flex={1} paddingTop={5}>
          <Typography level={'title-lg'}>Keine ToDos vorhanden</Typography>
        </Stack>
      )}
      <Stack
        gap={2}
        height={`${totalSize + theme.spacing(2)}px`}
        paddingTop={`${paddingTop}px`}
        paddingBottom={`${paddingBottom}px`}>
        {virtualItems.map((virtualRow) => {
          const task = filteredTasks[virtualRow.index];
          return (
            <Card key={virtualRow.key} data-index={virtualRow.index} ref={measureElement}>
              <TaskCardContent task={task} />
            </Card>
          );
        })}
      </Stack>
      {isFetchingNextPage && (
        <Stack alignItems={'center'} paddingY={2}>
          <CircularProgress size="sm" />
        </Stack>
      )}
    </>
  );
}

function TaskCardContent({ task }: { task: TaskDto }) {
  const embeddedPatient = task.journal?.patient ?? task.appointment?.patient ?? task.labOrder?.patient;
  const { data: fetchedPatient } = useGetPatientBasicsById(task.patientId, {
    query: { enabled: !embeddedPatient },
  });
  const patient = embeddedPatient ?? fetchedPatient;

  if (!patient) {
    return (
      <Stack alignItems={'center'} paddingTop={2}>
        <CircularProgress size={'sm'} />
      </Stack>
    );
  }

  return (
    <>
      {task.taskType === TaskDtoTaskType.NOTE_WITH_TODO && task.isDone !== null && (
        <TaskJournalCard task={task} patient={patient} />
      )}
      {task.taskType === TaskDtoTaskType.NOTE_WITH_TODO && task.isDone == null && (
        <TaskNotic task={task} patient={patient} />
      )}
      {task.taskType === TaskDtoTaskType.THERAPY_COMPLIANCE_OVERDUE && (
        <TaskTherapyComplianceOverdueCard task={task} patient={patient} />
      )}
      {task.taskType === TaskDtoTaskType.APPOINTMENT_TODAY && <TaskAppointmentCard task={task} patient={patient} />}
      {task.taskType === TaskDtoTaskType.TEST_RESULT_INCOMPLETE && <TaskLabOrderCard task={task} patient={patient} />}
      {task.taskType === TaskDtoTaskType.TRACING_WAITING_PERIOD_OVER && (
        <TaskWaitingPeriodOverCard task={task} patient={patient} />
      )}
    </>
  );
}
